/**
 * AdminTopBar (STORY-008-006). Logo + Admin badge + breadcrumb (left); health
 * pill + language switcher + theme toggle + email + logout (right); a hamburger
 * (<lg) toggling the mobile nav. --g-* tokens; ≥44px tap targets.
 */
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";

import { Button, Icon } from "@garden/ui";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ThemeToggle } from "@/components/ThemeToggle";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { ApiHealthPill } from "@/features/admin/components/ApiHealthPill";
import { Breadcrumb } from "@/features/admin/components/Breadcrumb";
import { logout, useMe } from "@/features/settings/api";

export function AdminTopBar({
  onMenuToggle,
}: {
  onMenuToggle?: () => void;
}): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const { data: profile } = useMe();
  const navigate = useNavigate();

  // Reuses the shared logout() (POST /auth/logout + clearAuth() +
  // queryClient.clear() + endLocalSession() — SCRUM-138/SCRUM-276), same as
  // AuthLoginScreen / SellerPortalShell. This used to be a hand-rolled copy
  // of that same sequence, which bypassed endLocalSession(): on a shared
  // admin terminal the previous admin's open chat thread survived the "Log
  // out" click (an SPA navigate() below, never a reload).
  const handleLogout = async (): Promise<void> => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <header
      data-testid="admin-top-bar"
      className="flex h-14 items-center gap-3 border-b border-[var(--g-hairline)] bg-[var(--g-surface)] px-4"
    >
      {/* Hamburger — mobile only (<lg); toggles the overlay drawer. */}
      <button
        type="button"
        data-testid="admin-mobile-menu-btn"
        aria-label={t("nav.openMenu")}
        onClick={onMenuToggle}
        className="inline-flex size-11 items-center justify-center rounded-lg text-[var(--g-secondary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] lg:hidden"
      >
        <Icon name="menu" size={20} />
      </button>

      {/* Logo + Admin badge */}
      <div className="flex items-center gap-2">
        <span
          aria-label={t("topbar.logoAlt")}
          className="text-lg font-bold tracking-tight text-[var(--g-primary)]"
        >
          GardEn
        </span>
        <span className="rounded bg-[var(--g-primary)] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
          Admin
        </span>
      </div>

      {/* Breadcrumb — hidden on the narrowest viewports. */}
      <div className="hidden md:block">
        <Breadcrumb />
      </div>

      <div className="flex-1" />

      {/* Right cluster: health · language · theme · email · logout */}
      <ApiHealthPill />
      <LanguageSwitcher className="hidden sm:inline-flex" />
      <ThemeToggle className="mr-1" />
      {profile?.email ? (
        <span
          data-testid="admin-user-email"
          className="mr-2 hidden text-sm text-[var(--g-secondary)] lg:block"
        >
          {profile.email}
        </span>
      ) : null}
      {/* SCRUM-182 — account settings live with the identity chip, not in the
          capability-driven side nav: managing your own name/password/sessions
          is not an ops permission, and adding it there would stop the
          Operations group ever hiding and the landing's empty state ever
          showing (both are asserted behaviours). */}
      <Link
        to="/admin/account"
        data-testid="admin-account-link"
        className="inline-flex min-h-[44px] items-center rounded-lg px-2 text-sm font-medium text-[var(--g-secondary)] underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
      >
        {t("account.title")}
      </Link>
      <Button
        data-testid="admin-logout-btn"
        variant="ghost"
        size="sm"
        onClick={() => void handleLogout()}
        className="min-h-[44px]"
      >
        {t("topbar.logout")}
      </Button>
    </header>
  );
}

export default AdminTopBar;
